import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LockIcon, UserIcon, EyeIcon, EyeOffIcon, AlertCircleIcon } from 'lucide-react';
interface LoginPageProps {
  onLogin: (role: 'admin' | 'basic') => void;
}
const VALID_USERS = [{
  username: 'sven',
  password: 'Letmein@0815',
  role: 'admin' as const
}, {
  username: 'salesagent',
  password: 'n3ocube@2026',
  role: 'admin' as const
}, {
  username: 'marcus',
  password: 'RS6-Audi@650',
  role: 'basic' as const
}];
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) {
      setError('Bitte füllen Sie alle Felder aus.');
      return;
    }
    setIsLoading(true);
    // Simulate brief loading for UX feel
    setTimeout(() => {
      const validUser = VALID_USERS.find(u => u.username === username && u.password === password);
      if (validUser) {
        onLogin(validUser.role);
      } else {
        setError('Benutzername oder Passwort ist falsch.');
        setIsLoading(false);
      }
    }, 400);
  };
  return <div className="min-h-screen w-full bg-stone-100 flex items-center justify-center px-4" data-id="element-150">
      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="w-full max-w-sm" data-id="element-151">
        {/* Logo & Title */}
        <div className="flex flex-col items-center mb-8" data-id="element-152">
          <img src="https://cdn.magicpatterns.com/uploads/5wGtjBXVfZ39gnw9DTVfMJ/IMG_3255.png" alt="PEZULA – We make future happen." className="object-contain mb-3" style={{
          width: '188px'
        }} data-id="element-153" />
          <p className="text-sm text-stone-500" data-id="element-154">PEZULA Financing Solutions</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200" data-id="element-155">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-156">
            {/* Username */}
            <div data-id="element-157">
              <label htmlFor="login-username" className="block text-sm font-medium text-stone-600 mb-1.5" data-id="element-158">
                Benutzername
              </label>
              <div className="relative" data-id="element-159">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-160" />
                <input id="login-username" type="text" value={username} onChange={e => {
                setUsername(e.target.value);
                setError('');
              }} placeholder="Benutzername" autoComplete="username" autoFocus className="w-full pl-10 pr-4 py-2.5 text-sm text-stone-900 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition-all" data-id="element-161" />
              </div>
            </div>

            {/* Password */}
            <div data-id="element-162">
              <label htmlFor="login-password" className="block text-sm font-medium text-stone-600 mb-1.5" data-id="element-163">
                Passwort
              </label>
              <div className="relative" data-id="element-164">
                <LockIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-165" />
                <input id="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => {
                setPassword(e.target.value);
                setError('');
              }} placeholder="Passwort" autoComplete="current-password" className="w-full pl-10 pr-11 py-2.5 text-sm text-stone-900 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition-all" data-id="element-166" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors" aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'} data-id="element-167">
                  {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-168" /> : <EyeIcon className="w-4 h-4" data-id="element-169" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && <motion.div initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl" data-id="element-170">
                <AlertCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" data-id="element-171" />
                <p className="text-sm text-red-700" data-id="element-172">{error}</p>
              </motion.div>}

            {/* Submit */}
            <button type="submit" disabled={isLoading} className="w-full py-2.5 bg-stone-700 text-white rounded-xl font-medium text-sm hover:bg-stone-800 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-173">
              {isLoading ? <span className="flex items-center justify-center gap-2" data-id="element-174">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-175">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" data-id="element-176" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-177" />
                  </svg>
                  Anmelden…
                </span> : 'Anmelden'}
            </button>
          </form>
        </div>

        <p className="text-xs text-stone-400 text-center mt-6" data-id="element-178">
          Zugang nur für autorisierte PEZULA-Partner.
        </p>
      </motion.div>
    </div>;
}